import { marketingVariantTypeSchema } from './product-research-inputs.js';
/**
 * What: Shared Zod snapshot contract for the Study Builder edit-source seam.
 * Transport / data-flow: Pure validation shared by the Shopify Worker producer
 * (`apps/shopify/src/study-setups.ts`) and the merchant form consumer
 * (`apps/shopify/app/hooks/study-builder/studyBuilderDrafts.ts`). No Worker, D1,
 * HTTP, scoring, persistence, or provider execution lives here.
 *
 * Why this exists: the Worker built its edit-source snapshot without `audienceDraft`
 * while the form read that key unconditionally, so the seam drifted silently until
 * the merchant form crashed. Both sides validate against this schema instead. The
 * producer and consumer both reject invalid wire snapshots (#4269); errors do not
 * become best-effort, empty or defaulted drafts. Form-native initialisation and
 * complete API snapshots have distinct schemas while sharing field definitions.
 *
 * The `audienceDraft` shape mirrors `inlineAudienceDraftSchema` from
 * `@testfeed/form-rendering` without importing it: this package is a dependency leaf
 * (zod only) and must not gain a dependency on a form-level package. Nested objects
 * use `catchall` so server-owned detail the form ignores (lever provenance, planner
 * route hints) survives validation instead of being stripped.
 */
import * as z from 'zod';
import { shopifyAudienceModeSchema } from './shopify-audience-mode.js';
const snapshotAudienceRoleSchema = z.enum([
    'eligibility',
    'quota',
    'conditioning',
    'segmentation',
    'diagnostic_only',
    'excluded',
    'scoring',
    'matched_panel_claim',
    'calibration_claim',
]);
const snapshotAudienceLeverSchema = z
    .object({
    id: z.string(),
    field: z.string(),
    label: z.string(),
    sourceType: z.literal('first_party_analytics'),
    groundingStatus: z.enum(['observed', 'sampled_from_marginal_observed', 'sampled_from_joint_observed']),
    targetDistribution: z.record(z.string(), z.number()).optional(),
    selectedRoles: z.array(snapshotAudienceRoleSchema).default([]),
    allowedRoles: z.array(snapshotAudienceRoleSchema).default([]),
    blockedRoles: z.array(snapshotAudienceRoleSchema).default([]),
    previewStatement: z.string(),
    claimBoundary: z.string(),
})
    .catchall(z.unknown());
const snapshotAudiencePlanSchema = z
    .object({
    baseAudience: z.object({
        id: z.literal('broad_population'),
        label: z.string(),
        sourceType: z.literal('population_field'),
    }),
    selectedFrame: shopifyAudienceModeSchema,
    levers: z.array(snapshotAudienceLeverSchema),
    savedAudienceSetId: z.string().optional(),
    savedAudienceSetName: z.string().optional(),
    populationId: z.string().optional(),
    marketCountry: z.string().optional(),
    audienceFilters: z
        .object({
        gender: z.enum(['All', 'Female', 'Male']),
        age_range: z.object({ min: z.number(), max: z.number() }).optional(),
        income_bands: z.array(z.string()).optional(),
        employment_statuses: z.array(z.string()).optional(),
    })
        .optional(),
})
    .catchall(z.unknown());
const snapshotAudienceDraftSchema = z
    .object({
    name: z.string().default(''),
    minimumAge: z.string().default('18'),
    maximumAge: z.string().default('65'),
    gender: z.enum(['All', 'Male', 'Female']).default('All'),
    incomeBands: z.array(z.string()).default([]),
    employmentStatuses: z.array(z.string()).default([]),
    marketCountry: z.enum(['AU', 'US', 'unsupported']).default('unsupported'),
    selectedStoreBehaviorLeverIds: z.array(z.string()).default([]),
    selectedCustomerGroupLeverId: z.string().nullable().default(null),
})
    .catchall(z.unknown());
function defaultSnapshotAudienceDraft() {
    return {
        name: '',
        minimumAge: '18',
        maximumAge: '65',
        gender: 'All',
        incomeBands: [],
        employmentStatuses: [],
        marketCountry: 'unsupported',
        selectedStoreBehaviorLeverIds: [],
        selectedCustomerGroupLeverId: null,
    };
}
const snapshotProductSchema = z
    .object({
    id: z.string().min(1),
    title: z.string(),
    handle: z.string().default(''),
    description: z.string().default(''),
    descriptionHtml: z.string().nullable().optional(),
    productType: z.string().default(''),
    vendor: z.string().default(''),
    tags: z.array(z.string()).default([]),
    /** Shopify product status as read from Shopify; null when it was never read (stored setups). */
    status: z.string().nullable().default(null),
    imageUrl: z.string().nullable().default(null),
    imageAlt: z.string().nullable().default(null),
    /** TestFeed media URLs for the product's pictures. Listed explicitly (not left to the catchall) so every read and
     * evaluate emit it in one fixed key position: a draft seeded with `productMedia` after `updatedAt` and an evaluate
     * snapshot with it before `updatedAt` would otherwise differ only by key order and flip the save bar on (#4487). */
    productMedia: z.array(z.string()).nullable().optional(),
    /** Shopify's own signal that the product has a single default variant (so no colour to choose). Absent on drafts read
     * before F1; callers also treat more than one variant in the list as "several" (`productHasSeveralVariants`). */
    hasOnlyDefaultVariant: z.boolean().optional(),
    /** Total variants Shopify reports for the product, so the in-app fallback picker can say when it lists only some. */
    variantsCount: z.number().nullable().optional(),
    variants: z
        .array(z.object({
        id: z.string(),
        title: z.string(),
        price: z.string(),
        compareAtPrice: z.string().nullable().optional(),
        selectedOptions: z.array(z.object({ name: z.string(), value: z.string() })).optional(),
    }))
        .default([]),
    collections: z
        .array(z.object({
        id: z.string(),
        title: z.string(),
        handle: z.string(),
    }))
        .optional(),
    /** When the stored product copy was last read from Shopify. Listed explicitly for the same fixed-key-order reason as
     * `productMedia` above (#4487). */
    updatedAt: z.string().nullable().optional(),
    currencyCode: z.string().nullable().optional(),
    claims: z.array(z.string()).optional(),
})
    .catchall(z.unknown());
const snapshotBriefDraftSchema = z.object({
    studyName: z.string().default(''),
    objective: z.string().default(''),
    decision: z.string().default(''),
    hypotheses: z.string().default(''),
    diagnosticPrompt: z.string().default(''),
});
export const studyBuilderResearchInputIdentitySchema = z.object({
    metafieldId: z.string(),
    namespace: z.string(),
    key: z.string(),
    valueFingerprint: z.string(),
    variantId: z.string().nullable(),
});
const snapshotTestElementSchema = z
    .object({
    id: z.string(),
    label: z.string().default(''),
    elementType: z.string().default(''),
    role: z.enum(['context', 'treatment', 'diagnostic_context', 'unsupported_request']),
    values: z.array(z.string()).default([]),
    // Stable per-option ids, index-aligned to `values`, made and owned by the Worker so an edit addresses an option by
    // its id (`comparisons.<id>.options.<optionId>`), not its moving position. Optional: older drafts have none and the
    // Worker mints them on read. Never read by saving/validation/preparation/Web — those read `values` only.
    valueIds: z.array(z.string()).optional(),
    // The next option number the Worker will mint (`option-<n>`). Monotonic: advanced on every mint, never lowered by a
    // Remove, so an option id is never reused and a late edit for a removed option cannot land on a new one. Optional:
    // minted on read as one past the highest existing id when a draft has none. Draft-only, like `valueIds`.
    nextOptionNumber: z.number().optional(),
    // The merchant's standing request for AI wording suggestions on this comparison, and the pinned batch it resolved to.
    // Presence means "suggestions were asked for". `requestId` is `'reuse'` for the first request and `after:<batchId>` for
    // each "Show another set" (deterministic, so a resent press never mints a second batch). `logicalBatchId` is pinned once
    // the coordinator claims a batch, so EVERY later evaluation READS that batch and never regenerates — a normal evaluation
    // (typing, Add, poll) must never spend. `canShowMore` is pinned when ready; `pendingSince` bounds how long a batch may
    // stay pending before it is reported unavailable; `failure` is a sticky failure code (no retry storm) cleared only when
    // the merchant presses again. Draft-only (like `valueIds`): never read by saving/validation/preparation/Web.
    suggestionRequest: z
        .object({
        requestId: z.string().min(1),
        logicalBatchId: z.string().min(1).optional(),
        // Draft-only digest; feedback follows another set only when its product, field and baseline context still match.
        contextFingerprint: z.string().min(1).optional(),
        canShowMore: z.boolean().optional(),
        pendingSince: z.string().min(1).optional(),
        failure: z.enum(['credits', 'plan', 'no_baseline', 'other']).optional(),
    })
        .nullable()
        .optional(),
    source: z.enum(['shopify_product', 'merchant_authored', 'derived_default']),
    benchmarkValue: z.string().optional(),
    comparisonType: marketingVariantTypeSchema.optional(),
    researchInput: studyBuilderResearchInputIdentitySchema.optional(),
    stimulusContract: z.string().optional(),
    treatmentContract: z.string().optional(),
    productId: z.string().optional(),
    variantIds: z.array(z.string()).optional(),
    sourceField: z
        .object({
        metafieldId: z.string(),
        namespace: z.string(),
        key: z.string(),
        valueFingerprint: z.string(),
        label: z.string(),
        value: z.string(),
    })
        .optional(),
})
    .catchall(z.unknown());
const snapshotSurveyQuestionSchema = z
    .object({
    id: z.string(),
    text: z.string().default(''),
    construct: z.string().optional(),
    scoreBearing: z.boolean().default(false),
    required: z.boolean().default(true),
    responseContract: z.literal('open_ended_explanation').default('open_ended_explanation'),
    stimulusContract: z.string().optional(),
    treatmentContract: z.string().optional(),
    linkedElementIds: z.array(z.string()).default([]),
    routeHint: z
        .enum(['ssr', 'diagnostic', 'pricing', 'comparative', 'feature_priority', 'conversion_lift'])
        .optional(),
    acknowledgedRuleIds: z.array(z.string()).optional(),
})
    .catchall(z.unknown());
// The draft carries ONLY `seed`. The AI model and the shopper count (sample
// size) belong to the account's settings (owned by Web), never the builder
// form. Old drafts that still hold `sampleSize`/`model` keep loading: this is a
// plain (non-strict) object, so those unknown keys are stripped on parse.
const snapshotExecutionSettingsSchema = z.object({
    seed: z.number().int().default(0),
});
/** Schema construction only: required wire values must not inherit form-initialisation defaults. */
function wireObject(shape) {
    const fields = Object.fromEntries(Object.entries(shape).map(([key, field]) => [key, field instanceof z.ZodDefault ? field.removeDefault() : field]));
    // The output fields are unchanged; only untrusted missing-input behaviour differs.
    return z.object(fields).catchall(z.unknown());
}
/**
 * Validates the full Study Builder state snapshot exchanged over the edit-source
 * seam. Strict enough to catch shape drift (missing keys, scalar types, unknown
 * enum members), lenient enough that a snapshot without `audienceDraft` passes
 * with the empty draft applied.
 */
export const studyBuilderSnapshotSchema = z
    .object({
    /** Server-owned catalog/market facts, not editable authoring. Evaluation may refresh them without marking a merchant
     * edit or acknowledging a Save. Product/variant identities, audience and comparison choices remain ordinary edits;
     * source-only changes still participate in whole-draft Save and uncertain-write reconciliation. */
    selectedProduct: snapshotProductSchema.nullable(),
    selectedProductId: z.string(),
    /** The one variant the test is about (owner, 2026-10-20; docs/concepts/shopify/comparison-control.md). Absent: none
     * chosen yet. Every per-variant value (price, the variant's pictures) follows it. Optional and left out when empty, so
     * a draft saved before it existed parses, hashes and compares exactly as before. */
    selectedVariantId: z.string().min(1).optional(),
    /** The typed filter for the version section's tile list (owner, 2026-10-20). The Worker filters the live variant list by
     * it; the client paces it like other typing edits. Optional and left out when empty, so a draft saved before it existed
     * parses, hashes and compares exactly as before. */
    variantSearch: z.string().optional(),
    selectedAudienceMode: shopifyAudienceModeSchema,
    selectedAudiencePlan: snapshotAudiencePlanSchema.nullable().default(null),
    audienceDraft: snapshotAudienceDraftSchema.default(defaultSnapshotAudienceDraft),
    briefDraft: snapshotBriefDraftSchema,
    testElements: z.array(snapshotTestElementSchema).default([]),
    surveyQuestions: z.array(snapshotSurveyQuestionSchema).default([]),
    executionSettings: snapshotExecutionSettingsSchema,
    productSelectionLocked: z.boolean().default(false),
})
    .superRefine((snapshot, ctx) => {
    if ((!snapshot.selectedProduct && snapshot.selectedProductId.trim()) ||
        (snapshot.selectedProduct && snapshot.selectedProduct.id !== snapshot.selectedProductId))
        ctx.addIssue({
            code: 'custom',
            path: ['selectedProductId'],
            message: 'STUDY_BUILDER_SOURCE_MISMATCH: Product identities disagree.',
        });
    if (snapshot.selectedVariantId && !snapshot.selectedProductId.trim())
        ctx.addIssue({
            code: 'custom',
            path: ['selectedVariantId'],
            message: 'STUDY_BUILDER_SOURCE_MISMATCH: A variant is chosen without a product.',
        });
});
/** Complete wire fields, shared by API snapshots and stored drafts (which keep the v1 lock default). */
export const studyBuilderWireSnapshotFields = {
    selectedProduct: wireObject(snapshotProductSchema.shape).nullable(),
    selectedAudiencePlan: wireObject(snapshotAudiencePlanSchema.shape).nullable(),
    audienceDraft: wireObject(snapshotAudienceDraftSchema.shape),
    briefDraft: wireObject(snapshotBriefDraftSchema.shape),
    testElements: z.array(wireObject(snapshotTestElementSchema.shape)),
    surveyQuestions: z.array(wireObject(snapshotSurveyQuestionSchema.shape)),
    executionSettings: z.object({
        seed: z.number().int().nonnegative(),
    }),
};
/** Complete API snapshots are distinct from the form-native empty snapshot. */
export const studyBuilderWireSnapshotSchema = studyBuilderSnapshotSchema.safeExtend({
    ...studyBuilderWireSnapshotFields,
    productSelectionLocked: z.boolean(),
});
/** Parses an untrusted Study Builder snapshot or throws a structured Zod error. */
export function parseStudyBuilderSnapshot(value) {
    return studyBuilderSnapshotSchema.parse(value);
}
/** Safely validates an untrusted Study Builder snapshot without throwing. */
export function safeParseStudyBuilderSnapshot(value) {
    return studyBuilderSnapshotSchema.safeParse(value);
}
//# sourceMappingURL=shopify-study-builder-snapshot.js.map